import { useEffect, useMemo, useState } from "react";
import { GripVertical, ImagePlus, Layers, Pencil, Plus, Trash2 } from "lucide-react";
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { api } from "@/lib/api";
import { extractErrorMessage } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { PageHeader, PageBody, EmptyState } from "@/components/PageChrome";
import { OptionIconEditor } from "@/components/OptionIconEditor";
import { slugifyKey as slugify } from "@/lib/slugify";
import { useTabTitle } from "@/lib/tabs";

const FIELD_TYPES = [
  { value: "text", group: "Basic" },
  { value: "longtext", group: "Basic" },
  { value: "richtext", group: "Basic" },
  { value: "number", group: "Numeric" },
  { value: "currency", group: "Numeric" },
  { value: "boolean", group: "Basic" },
  { value: "date", group: "Date & time" },
  { value: "datetime", group: "Date & time" },
  { value: "dropdown", group: "Choice" },
  { value: "multi_select", group: "Choice" },
  { value: "email", group: "Contact" },
  { value: "phone", group: "Contact" },
  { value: "url", group: "Contact" },
  { value: "member", group: "People" },
  { value: "image", group: "Media" },
  { value: "file", group: "Media" },
];

const emptyForm = () => ({
  id: null,
  label: "",
  key: "",
  type: "text",
  help_text: "",
  unit: "",
  custom_type_id: null,
  optionsText: "",
  optionIcons: {},
  min: "",
  max: "",
});

const formFromLib = (f) => ({
  id: f.id,
  label: f.label || "",
  key: f.key || "",
  type: f.type || "text",
  help_text: f.help_text || "",
  unit: f.unit || "",
  custom_type_id: f.custom_type_id || null,
  optionsText: Array.isArray(f.config?.options) ? f.config.options.join("\n") : "",
  optionIcons: f.config?.option_icons || {},
  min: f.config?.min ?? "",
  max: f.config?.max ?? "",
});

export default function LibraryFieldsPage() {
  const [library, setLibrary] = useState([]);
  const [customTypes, setCustomTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [keyTouched, setKeyTouched] = useState(false);
  // Custom-type mini-dialog
  const [typeOpen, setTypeOpen] = useState(false);
  const [typeForm, setTypeForm] = useState({ name: "", base_type: "number", unit: "" });
  const [savingType, setSavingType] = useState(false);

  const isEditing = !!form.id;

  useTabTitle("Fields", "layers");

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const persistOrder = async (ordered) => {
    const prev = library;
    setLibrary(ordered);
    try {
      await api.post("/field-library/reorder", { order: ordered.map((f) => f.id) });
    } catch (err) {
      toast.error(extractErrorMessage(err));
      setLibrary(prev); // rollback
    }
  };

  const onDragEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = library.findIndex((f) => f.id === active.id);
    const to = library.findIndex((f) => f.id === over.id);
    if (from === -1 || to === -1) return;
    persistOrder(arrayMove(library, from, to));
  };

  const load = async () => {
    try {
      const [libRes, ctRes] = await Promise.all([
        api.get("/field-library"),
        api.get("/field-types"),
      ]);
      setLibrary(Array.isArray(libRes.data) ? libRes.data : []);
      setCustomTypes(Array.isArray(ctRes.data) ? ctRes.data : []);
    } catch (e) {
      toast.error(extractErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const customTypeById = useMemo(
    () => Object.fromEntries(customTypes.map((c) => [c.id, c])),
    [customTypes],
  );

  const applyCustomType = (ct) => {
    setForm((f) => ({
      ...f,
      type: ct.base_type,
      unit: ct.unit || "",
      custom_type_id: ct.id,
      optionsText: Array.isArray(ct.default_config?.options)
        ? ct.default_config.options.join("\n") : f.optionsText,
      min: ct.default_config?.min ?? f.min,
      max: ct.default_config?.max ?? f.max,
    }));
  };

  const onTypeChange = (v) => {
    if (v === "__create_type__") {
      setTypeForm({ name: "", base_type: "number", unit: "" });
      setTypeOpen(true);
      return;
    }
    if (v.startsWith("ct:")) {
      const ct = customTypeById[v.slice(3)];
      if (ct) applyCustomType(ct);
      return;
    }
    setForm((f) => ({ ...f, type: v, custom_type_id: null }));
  };

  const submitType = async (e) => {
    e.preventDefault();
    setSavingType(true);
    try {
      const r = await api.post("/field-types", {
        name: typeForm.name,
        base_type: typeForm.base_type,
        unit: typeForm.unit || null,
      });
      toast.success(`Type '${typeForm.name}' created`);
      setCustomTypes((prev) => [...prev, r.data]);
      applyCustomType(r.data);
      setTypeOpen(false);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setSavingType(false);
    }
  };

  const openForCreate = () => {
    setForm(emptyForm());
    setKeyTouched(false);
    setOpen(true);
  };

  const openForEdit = (f) => {
    setForm(formFromLib(f));
    setKeyTouched(true);
    setOpen(true);
  };

  const buildPayload = () => {
    const config = {};
    if (form.type === "dropdown" || form.type === "multi_select") {
      const opts = form.optionsText.split(/\n|,/).map((s) => s.trim()).filter(Boolean);
      config.options = opts;
      const icons = {};
      opts.forEach((o) => { if (form.optionIcons?.[o]) icons[o] = form.optionIcons[o]; });
      if (Object.keys(icons).length) config.option_icons = icons;
    }
    if (form.type === "number" || form.type === "currency") {
      if (form.min !== "") config.min = Number(form.min);
      if (form.max !== "") config.max = Number(form.max);
    }
    return {
      label: form.label,
      key: form.key,
      type: form.type,
      help_text: form.help_text || null,
      unit: form.unit || null,
      custom_type_id: form.custom_type_id || null,
      config,
    };
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEditing) {
        // key & custom_type_id are immutable once created — never send them on edit.
        const { key, custom_type_id, ...patch } = buildPayload();
        await api.patch(`/field-library/${form.id}`, patch);
        toast.success(`Field '${form.label}' updated`);
      } else {
        await api.post("/field-library", buildPayload());
        toast.success(`Field '${form.label}' created`);
      }
      setOpen(false);
      setForm(emptyForm());
      setKeyTouched(false);
      await load();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (f) => {
    if (!window.confirm(
      `Delete field "${f.label}" from your library?\n\nIt will be removed from every collection that uses it. This cannot be undone.`
    )) return;
    try {
      await api.delete(`/field-library/${f.id}`);
      toast.success(`Field '${f.label}' deleted`);
      await load();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const groupedTypes = useMemo(() => {
    const map = {};
    FIELD_TYPES.forEach((ft) => {
      map[ft.group] = map[ft.group] || [];
      map[ft.group].push(ft.value);
    });
    return map;
  }, []);

  const supportsOptions = form.type === "dropdown" || form.type === "multi_select";
  const supportsMinMax = form.type === "number" || form.type === "currency";
  const supportsUnit = ["number", "currency", "text", "longtext"].includes(form.type);
  const optionValues = supportsOptions
    ? form.optionsText.split(/\n|,/).map((s) => s.trim()).filter(Boolean)
    : [];

  const setOptionIcon = (value, mediaId) => {
    setForm((f) => {
      const next = { ...(f.optionIcons || {}) };
      if (mediaId) next[value] = mediaId; else delete next[value];
      return { ...f, optionIcons: next };
    });
  };

  return (
    <>
      <PageHeader
        title="Fields"
        subtitle="Reusable fields for your whole workspace. Create a field once here, then add it to any collection — edits apply everywhere it's used."
        breadcrumbs={[{ label: "Library" }, { label: "Fields" }]}
        actions={
          <Button onClick={openForCreate} data-testid="new-library-field-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Add field
          </Button>
        }
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="library-field-dialog">
          <form onSubmit={submit}>
            <DialogHeader>
              <DialogTitle>{isEditing ? "Edit field" : "Add field"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Label</Label>
                  <Input
                    data-testid="library-field-input-label"
                    value={form.label}
                    onChange={(e) => {
                      const v = e.target.value;
                      setForm((f) => ({
                        ...f,
                        label: v,
                        key: keyTouched ? f.key : slugify(v),
                      }));
                    }}
                    placeholder="SKU"
                    required
                  />
                </div>
                <div>
                  <Label>
                    Key{" "}
                    {isEditing && (
                      <span className="text-xs text-muted-foreground font-normal">
                        (can't change)
                      </span>
                    )}
                  </Label>
                  <Input
                    data-testid="library-field-input-key"
                    className="font-mono"
                    value={form.key}
                    disabled={isEditing}
                    onChange={(e) => {
                      setKeyTouched(true);
                      setForm((f) => ({ ...f, key: slugify(e.target.value) }));
                    }}
                    placeholder="sku"
                    required
                  />
                </div>
              </div>
              <div>
                <Label>Type</Label>
                <Select
                  value={form.custom_type_id ? `ct:${form.custom_type_id}` : form.type}
                  onValueChange={onTypeChange}
                  disabled={isEditing && !!form.custom_type_id}
                >
                  <SelectTrigger data-testid="library-field-input-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {customTypes.length > 0 && (
                      <div>
                        <div className="px-2 py-1.5 text-[10px] font-mono uppercase tracking-wide text-muted-foreground">
                          Your types
                        </div>
                        {customTypes.map((ct) => (
                          <SelectItem key={ct.id} value={`ct:${ct.id}`}>
                            {ct.name}
                            <span className="text-muted-foreground">
                              {" "}· {ct.base_type}{ct.unit ? ` (${ct.unit})` : ""}
                            </span>
                          </SelectItem>
                        ))}
                      </div>
                    )}
                    {Object.entries(groupedTypes).map(([g, arr]) => (
                      <div key={g}>
                        <div className="px-2 py-1.5 text-[10px] font-mono uppercase tracking-wide text-muted-foreground">
                          {g}
                        </div>
                        {arr.map((v) => (
                          <SelectItem key={v} value={v}>{v}</SelectItem>
                        ))}
                      </div>
                    ))}
                    <div className="border-t border-border mt-1">
                      <SelectItem value="__create_type__">
                        ＋ Create a field type…
                      </SelectItem>
                    </div>
                  </SelectContent>
                </Select>
                {form.custom_type_id && customTypeById[form.custom_type_id] && (
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Using your <b>{customTypeById[form.custom_type_id].name}</b> type
                    {" "}(base: {form.type}{form.unit ? `, unit ${form.unit}` : ""}).
                  </p>
                )}
              </div>

              {supportsOptions && (
                <div>
                  <Label>
                    Options{" "}
                    <span className="text-xs text-muted-foreground font-normal">
                      (one per line, or comma-separated)
                    </span>
                  </Label>
                  <Textarea
                    data-testid="library-field-input-options"
                    rows={3}
                    value={form.optionsText}
                    onChange={(e) => setForm((f) => ({ ...f, optionsText: e.target.value }))}
                    placeholder={"chair\ntable\nsofa"}
                  />
                </div>
              )}
              {supportsOptions && optionValues.length > 0 && (
                <OptionIconEditor
                  options={optionValues}
                  icons={form.optionIcons || {}}
                  onSet={setOptionIcon}
                />
              )}
              {supportsMinMax && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Min</Label>
                    <Input
                      type="number" step="any" value={form.min}
                      onChange={(e) => setForm((f) => ({ ...f, min: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Max</Label>
                    <Input
                      type="number" step="any" value={form.max}
                      onChange={(e) => setForm((f) => ({ ...f, max: e.target.value }))}
                    />
                  </div>
                </div>
              )}
              {supportsUnit && (
                <div>
                  <Label>
                    Unit{" "}
                    <span className="text-xs text-muted-foreground font-normal">
                      (shown after the value, e.g. GSM)
                    </span>
                  </Label>
                  <Input
                    data-testid="library-field-input-unit"
                    value={form.unit}
                    onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))}
                    placeholder="GSM"
                  />
                </div>
              )}
              <div>
                <Label>Help text</Label>
                <Input
                  value={form.help_text}
                  onChange={(e) => setForm((f) => ({ ...f, help_text: e.target.value }))}
                />
              </div>
              <p className="text-[11px] text-muted-foreground">
                Per-collection settings like <b>required</b> or <b>unique</b> are set on each
                collection's own Fields page.
              </p>
            </div>
            <DialogFooter>
              <Button variant="ghost" type="button" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="submit-library-field">
                {saving
                  ? isEditing ? "Saving…" : "Adding…"
                  : isEditing ? "Save changes" : "Add field"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create-a-custom-type mini dialog */}
      <Dialog open={typeOpen} onOpenChange={setTypeOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="library-field-type-dialog">
          <form onSubmit={submitType}>
            <DialogHeader>
              <DialogTitle>Create a field type</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Name</Label>
                <Input
                  value={typeForm.name}
                  onChange={(e) => setTypeForm((t) => ({ ...t, name: e.target.value }))}
                  placeholder="GSM"
                  required
                  autoFocus
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Base type</Label>
                  <Select
                    value={typeForm.base_type}
                    onValueChange={(v) => setTypeForm((t) => ({ ...t, base_type: v }))}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(groupedTypes).map(([g, arr]) => (
                        <div key={g}>
                          <div className="px-2 py-1.5 text-[10px] font-mono uppercase tracking-wide text-muted-foreground">
                            {g}
                          </div>
                          {arr.map((v) => (
                            <SelectItem key={v} value={v}>{v}</SelectItem>
                          ))}
                        </div>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Unit</Label>
                  <Input
                    value={typeForm.unit}
                    onChange={(e) => setTypeForm((t) => ({ ...t, unit: e.target.value }))}
                    placeholder="GSM"
                  />
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground">
                A reusable type (e.g. GSM = number + unit “GSM”). Selecting it prefills a field's base type and unit.
              </p>
            </div>
            <DialogFooter>
              <Button variant="ghost" type="button" onClick={() => setTypeOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={savingType}>
                {savingType ? "Creating…" : "Create type"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <PageBody>
        {loading ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : library.length === 0 ? (
          <EmptyState
            icon={Layers}
            title="No fields in your library yet"
            description="Create a reusable field — text, number, dropdown, and more — then add it to any collection."
            action={
              <Button onClick={openForCreate} data-testid="empty-new-library-field">
                <Plus className="w-4 h-4 mr-1.5" /> Add first field
              </Button>
            }
            testId="library-fields-empty"
          />
        ) : (
          <>
            {library.some((f) => f.type === "dropdown" || f.type === "multi_select") && (
              <div className="mb-3 flex items-start gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground" data-testid="option-icon-hint">
                <ImagePlus className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
                <span>
                  <b className="text-foreground">Print icons:</b> edit a dropdown or multi-select field
                  (e.g. <span className="font-medium">End Use</span>) to upload an icon for each option.
                  They print on labels when “Show icons” is enabled in the Print dialog.
                </span>
              </div>
            )}
            <div className="rounded-lg border border-border bg-white overflow-hidden">
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8" />
                    <TableHead>Label</TableHead>
                    <TableHead>Key</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <SortableContext items={library.map((f) => f.id)} strategy={verticalListSortingStrategy}>
                    {library.map((f) => (
                      <SortableLibraryFieldRow
                        key={f.id}
                        field={f}
                        typeName={f.custom_type_id ? customTypeById[f.custom_type_id]?.name : null}
                        onEdit={openForEdit}
                        onDelete={remove}
                      />
                    ))}
                  </SortableContext>
                </TableBody>
              </Table>
            </DndContext>
            </div>
          </>
        )}
      </PageBody>
    </>
  );
}

function SortableLibraryFieldRow({ field: f, typeName, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: f.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };
  return (
    <TableRow ref={setNodeRef} style={style} data-testid={`library-field-row-${f.key}`}>
      <TableCell className="w-8 pr-0">
        <button
          type="button"
          className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground touch-none p-1"
          aria-label="Drag to reorder"
          data-testid={`library-field-drag-${f.key}`}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="w-4 h-4" />
        </button>
      </TableCell>
      <TableCell className="font-medium">{f.label}</TableCell>
      <TableCell className="font-mono text-xs">{f.key}</TableCell>
      <TableCell>
        <Badge variant="secondary" className="font-mono text-[10px]">{typeName || f.type}</Badge>
        {typeName && <span className="ml-1 font-mono text-[10px] text-muted-foreground">{f.type}</span>}
        {f.unit && <span className="ml-1 font-mono text-[10px] text-muted-foreground">· {f.unit}</span>}
      </TableCell>
      <TableCell className="text-right">
        <div className="inline-flex gap-1">
          <Button
            variant="ghost" size="icon" className="h-8 w-8"
            onClick={() => onEdit(f)}
            data-testid={`library-field-edit-${f.key}`}
            aria-label="Edit field"
          >
            <Pencil className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost" size="icon"
            className="h-8 w-8 text-muted-foreground hover:text-destructive"
            onClick={() => onDelete(f)}
            data-testid={`library-field-delete-${f.key}`}
            aria-label="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}
